"use client";

import { use, useEffect, useState } from "react";
import { Box, Button, Callout, Flex, Text, Container } from "@radix-ui/themes";
import FontAwesome from "../utilities/FontAwesome";
import { setCookie } from "../../utils/StringUtilities";
import { useStores } from "../../stores/useStores";
import { DISMISSED_COOKIE } from "./utils";
import { TranslatedText } from "../utilities/TranslatedText";

const EXPIRATION_DAYS = 28;

export function PwaInstallBanner(props: {
  showPwaInstallBannerPromise: Promise<boolean>;
}) {
  const { interfaceStore } = useStores();
  const [dismissed, setDismissed] = useState(true);

  const shouldShowBanner = use(props.showPwaInstallBannerPromise);

  useEffect(() => {
    if (shouldShowBanner && !interfaceStore.isPWA) {
      setDismissed(false);
    }
  }, []);

  const [openSection, setOpenSection] = useState<"ios" | "android" | null>(
    null,
  );

  function toggleSection(section: "ios" | "android") {
    setOpenSection((prev) => (prev === section ? null : section));
  }

  if (dismissed) return null;

  async function dismiss() {
    setDismissed(true);
    await setCookie(DISMISSED_COOKIE, "true", EXPIRATION_DAYS);
  }

  return (
    <Container mt={"4"}>
      <Callout.Root
        color="gray"
        mb="4"
        style={{ gridTemplateColumns: "auto 1fr" }}
      >
        <Callout.Icon style={{ gridColumnStart: 1 }}>
          <FontAwesome prefix="fas" name="fa-mobile-screen" />
        </Callout.Icon>
        <Flex
          align="start"
          justify="between"
          gap="3"
          style={{ gridColumnStart: 2 }}
        >
          <Callout.Text>
            <Flex direction="column" gap="2">
              <Text>
                <Box>
                  <TranslatedText weight="bold">
                    Get notifications for new messages.
                  </TranslatedText>
                </Box>
                <TranslatedText>
                  Add this app to your phone&apos;s home screen to receive
                  alerts even when the browser is closed.
                </TranslatedText>
              </Text>

              <Flex gap={"3"} wrap={"wrap"}>
                <Button
                  variant="soft"
                  color="gray"
                  size="1"
                  onClick={() => toggleSection("ios")}
                  style={{
                    border: `1px solid ${openSection === "ios" ? "var(--gray-9)" : "transparent"}`,
                  }}
                >
                  <FontAwesome
                    prefix="fas"
                    name={
                      openSection === "ios"
                        ? "fa-chevron-up"
                        : "fa-chevron-down"
                    }
                  />
                  <TranslatedText ml="1">
                    How to install on iPhone / iPad
                  </TranslatedText>
                </Button>
                <Button
                  variant="soft"
                  color="gray"
                  size="1"
                  onClick={() => toggleSection("android")}
                  style={{
                    border: `1px solid ${openSection === "android" ? "var(--gray-9)" : "transparent"}`,
                  }}
                >
                  <FontAwesome
                    prefix="fas"
                    name={
                      openSection === "android"
                        ? "fa-chevron-up"
                        : "fa-chevron-down"
                    }
                  />
                  <TranslatedText ml="1">
                    How to install on Android
                  </TranslatedText>
                </Button>
              </Flex>

              {openSection === "ios" && (
                <Flex direction="column" gap="1" mt="2" ml="3">
                  <TranslatedText size="2">
                    <Text weight="bold">1.</Text> Open this page in{" "}
                    <Text weight="bold">Safari</Text>.
                  </TranslatedText>
                  <TranslatedText size="2">
                    <Text weight="bold">2.</Text> Tap the&nbsp;
                    <FontAwesome prefix="fas" name="fa-arrow-up-from-bracket" />
                    &nbsp;
                    <Text weight="bold">Share</Text> button at the bottom of the
                    screen.
                  </TranslatedText>
                  <TranslatedText size="2">
                    <Text weight="bold">3.</Text> Scroll down and tap{" "}
                    <Text weight="bold">Add to Home Screen</Text>.
                  </TranslatedText>
                  <TranslatedText size="2">
                    <Text weight="bold">4.</Text> Tap{" "}
                    <Text weight="bold">Add</Text> in the top-right corner.
                  </TranslatedText>
                  <TranslatedText size="2" mt="1">
                    Once installed, open the app from your home screen and allow
                    notifications when prompted.
                  </TranslatedText>
                </Flex>
              )}

              {openSection === "android" && (
                <Flex direction="column" gap="1" mt="2" ml="3">
                  <TranslatedText size="2">
                    Open this page in <Text weight="bold">Chrome</Text> and tap{" "}
                    <Text weight="bold">Install App</Text> when the prompt
                    appears at the top of the screen.
                  </TranslatedText>
                  <TranslatedText size="2" mt="1">
                    Once installed, open the app from your home screen and allow
                    notifications when prompted.
                  </TranslatedText>
                </Flex>
              )}
            </Flex>
          </Callout.Text>
          <Button
            variant="ghost"
            color="gray"
            size="1"
            onClick={dismiss}
            aria-label="Dismiss"
          >
            <FontAwesome prefix="fas" name="fa-xmark" />
          </Button>
        </Flex>
      </Callout.Root>
    </Container>
  );
}
